import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal, Input } from "antd";
import { useAuth } from "../../../context/AuthContext";
import useSet from "../../../hooks/common/useSet";
import { API_MAKE_PAYMENT } from "../../../utils/apis";
import toast from "react-hot-toast";
import styled from "styled-components";
import { Create } from "@mui/icons-material";
import { formatCurrency } from "../../../utils/helpers";
import { useToasts } from "../../../hooks/common/useToasts";
import { EmptyWrapper } from "../../common/Elements";
import { URL_UTILITY_TRANSACTIONS } from "../../../utils/urls";

const { OTP } = Input;

const PAYMENT_DETAILS_CONFIG: Record<string, string[]> = {
  NWSC: [
    "customer_name",
    "customer_no",
    "customer_area",
    "outstanding_balance",
    "phone_number",
    "item_name",
    "date",
    "transaction_id",
  ],
  URA: [
    "prn",
    "customer_name",
    "expiry_date",
    "prn_status_desc",
    "item_name",
    "date",
    "transaction_id",
  ],
};

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  prn: "PRN",
  customer_no: "Meter Number",
  transaction_id: "Transaction ID",
  item_name: "Utility Type",
  prn_status_desc: "PRN Status",
  phone_number: "Phone Number",
  outstanding_balance: "Outstanding Balance",
};

const PRN_STATUS_MESSAGES: Record<string, string> = {
  T: "PRN already paid.",
  R: "PRN received but not credited.",
  D: "PRN dishonored.",
  C: "PRN cancelled.",
  X: "PRN expired.",
  N: "Invalid PRN.",
};

const SESSION_TIMEOUT = 2 * 60 * 1000;

const UtilityPaymentsConfirm = () => {
  const { error, uploadData, success, submitting } = useSet();
  const [pin, setPin] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { tranToken } = useAuth();
  const location = useLocation();
  const transferData = location.state?.data || {};
  const formData = location.state?.formData || {};

  const formatLabel = (key: string): string => {
    if (FIELD_LABEL_OVERRIDES[key]) {
      return FIELD_LABEL_OVERRIDES[key];
    }

    return key
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderValue = (value: any, key?: string): React.ReactNode => {
    if (value === null || value === undefined || value === "") {
      return <EmptyWrapper>Empty</EmptyWrapper>;
    }

    if (
      ["amount", "charge", "debit_amount", "outstanding_balance"].includes(
        key || "",
      )
    ) {
      return formatCurrency(value);
    }

    if (["phone_number"].includes(key || "")) {
      return value.replace("256", "0");
    }

    if (key === "date") {
      return new Date(value).toLocaleString();
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      return (
        <NestedTable>
          <tbody>
            {Object.keys(value).map((nestedKey) => (
              <tr key={nestedKey}>
                <th>{formatLabel(nestedKey)}</th>
                <td>{renderValue(value[nestedKey], nestedKey)}</td>
              </tr>
            ))}
          </tbody>
        </NestedTable>
      );
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return String(value);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setPin("");
  };

  const handleCancelPayment = () => {
    navigate("/utility-payments/create", {
      state: { formData },
    });
  };

  const handleConfirmPayment = () => {
    if (!pin || pin.length !== 4) {
      toast.error("Please enter a 4-digit PIN");
      return;
    }

    uploadData({
      api: API_MAKE_PAYMENT,
      params: {
        transaction_pin: Number(pin),
        tran_token: tranToken,
        transaction_id: transferData.transaction_id,
      },
      method: "POST",
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      toast.error("Validation session has expired. Please try again.");
      navigate("/utility-payments/create", {
        state: { formData },
      });
    }, SESSION_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (success) {
      setIsModalOpen(false);
      toast.success("Payment confirmed successfully");
      navigate(`/register${URL_UTILITY_TRANSACTIONS}`);
    }
  }, [success, navigate]);

  useToasts({
    success,
    error,
    redirect: "reload",
    successMessage: "Operation Successfull",
  });

  return (
    <>
      <Wrapper className="flexColumn gap20">
        <TitleWrapper className="flexNullCenter gap10 x100 whiteBg radius10">
          <Create fontSize="small" color="primary" />
          <p
            className="capitalize"
            style={{
              fontWeight: "bold",
              marginBottom: 0,
            }}
          >
            Confirm Your Payment
          </p>
        </TitleWrapper>

        <GridContainer>
          {/* Left Column - Payment Details */}
          <DetailsSection className="whiteBg radius10">
            <SectionTitle>Payment Details</SectionTitle>
            <DetailsTable>
              <tbody>
                {(() => {
                  const paymentType = transferData.item_name || "";
                  const allowedFields =
                    PAYMENT_DETAILS_CONFIG[paymentType] || [];

                  // If no config for this payment type, show all except monetary fields
                  const fieldsToDisplay =
                    allowedFields.length > 0
                      ? allowedFields
                      : Object.keys(transferData).filter(
                          (key) =>
                            ![
                              "id",
                              "amount",
                              "charge",
                              "debit_amount",
                            ].includes(key),
                        );

                  return fieldsToDisplay
                    .filter((key) => transferData[key] !== undefined)
                    .map((key) => (
                      <tr key={key}>
                        <th>{formatLabel(key)}</th>
                        <td>{renderValue(transferData[key], key)}</td>
                      </tr>
                    ));
                })()}
              </tbody>
            </DetailsTable>
          </DetailsSection>

          {/* Right Column - Payment Summary */}
          <SummarySection className="whiteBg radius10">
            <SectionTitle>Payment Summary</SectionTitle>
            <SummaryContent>
              {transferData.item_name !== "URA" ||
              transferData.prn_status_code === "A" ? (
                <>
                  <SummaryRow>
                    <SummaryLabel>Amount</SummaryLabel>
                    <SummaryValue>
                      {formatCurrency(transferData.amount)}
                    </SummaryValue>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryLabel>Charge</SummaryLabel>
                    <SummaryValue>
                      {formatCurrency(transferData.charge)}
                    </SummaryValue>
                  </SummaryRow>
                  <SummaryDivider />
                  <SummaryRow className="total">
                    <SummaryLabel>Total (Incl. of all charges)</SummaryLabel>
                    <SummaryTotal>
                      {formatCurrency(transferData.debit_amount)}
                    </SummaryTotal>
                  </SummaryRow>
                  <PayButton
                    type="primary"
                    size="large"
                    onClick={showModal}
                    block
                  >
                    Pay Now
                  </PayButton>
                </>
              ) : (
                <StatusMessage>
                  {PRN_STATUS_MESSAGES[transferData.prn_status_code] ||
                    "Payment cannot be processed."}
                </StatusMessage>
              )}
              <CancelButton size="small" onClick={handleCancelPayment} block>
                Cancel
              </CancelButton>
            </SummaryContent>
          </SummarySection>
        </GridContainer>
      </Wrapper>

      {/* PIN Confirmation Modal */}
      <Modal
        title="Enter PIN to Confirm Payment"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        centered
        width={400}
      >
        <ModalContent>
          <ModalAmount>
            <strong>{formatCurrency(transferData.debit_amount)}</strong> is
            about to be deducted from your account
          </ModalAmount>
          <PinLabel>Enter your onePayX PIN</PinLabel>
          <OTP
            length={4}
            value={pin}
            onChange={setPin}
            size="large"
            mask="*"
            style={{ marginBottom: 20 }}
          />
          <Button
            type="primary"
            size="large"
            onClick={handleConfirmPayment}
            loading={submitting}
            disabled={
              submitting || !pin || pin.length !== 4 || pin.includes(" ")
            }
            block
          >
            Confirm Payment
          </Button>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UtilityPaymentsConfirm;

const Wrapper = styled.div`
  padding: 0 20px 20px 20px;
`;

const TitleWrapper = styled.div`
  padding: 10px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const DetailsSection = styled.div`
  padding: 20px;
  border: 1px solid #e8e8e8;
`;

const SummarySection = styled.div`
  padding: 20px;
  border: 1px solid #e8e8e8;
  height: fit-content;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #262626;
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &.total {
    padding-top: 16px;
  }
`;

const SummaryLabel = styled.span`
  font-size: 14px;
  color: #595959;
`;

const SummaryValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`;

const SummaryTotal = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #1890ff;
`;

const SummaryDivider = styled.div`
  height: 1px;
  background-color: #e8e8e8;
  margin: 8px 0;
`;

const PayButton = styled(Button)`
  margin-top: 16px;
  height: 45px;
  font-size: 16px;
  font-weight: 600;
`;

const CancelButton = styled(Button)`
  margin-top: 8px;
  height: 45px;
  font-size: 16px;
  font-weight: 600;
  border-color: #d9d9d9;
  color: #595959;

  &:hover {
    border-color: #ff4d4f;
    color: #ff4d4f;
  }
`;

const ModalContent = styled.div`
  padding: 20px 0;
  display: flex;
  flex-direction: column;
`;

const ModalAmount = styled.p`
  font-size: 14px;
  margin-bottom: 24px;
  text-align: center;
  color: #595959;

  strong {
    color: #1890ff;
    font-size: 18px;
  }
`;

const PinLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
  color: #262626;
`;

const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  font-size: 13px;

  tbody tr {
    border-bottom: 1px solid #f0f0f0;
  }

  tbody tr:nth-child(odd) {
    background-color: #fafafa;
  }

  th {
    text-align: left;
    padding: 10px 12px;
    font-weight: 600;
    width: 35%;
    color: #595959;
    font-size: 12px;
  }

  td {
    padding: 10px 12px;
    color: #262626;
    font-size: 13px;
  }
`;

const StatusMessage = styled.div`
  padding: 20px;
  background-color: #fff7e6;
  border: 1px solid #ffa940;
  border-radius: 6px;
  color: #d46b08;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin-bottom: 16px;
`;

const NestedTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin: 0;

  th {
    text-align: left;
    padding: 6px 12px;
    font-weight: 500;
    width: 40%;
    color: #595959;
    background-color: #fafafa;
    border: 1px solid #e8e8e8;
  }

  td {
    padding: 6px 12px;
    color: #595959;
    border: 1px solid #e8e8e8;
  }
`;
